/* eslint-disable consistent-return */
/* eslint-disable fp/no-mutating-methods */

import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppRootState, Status } from 'src/store/store'
import axios, { pluckData } from 'src/utilities/http-client'
import { concat, eqBy, groupBy, uniqBy, uniqWith, without } from 'ramda'
import {
  Accreditation,
  Certificate,
  Class,
  ClassStreamEvent,
  ConnectClient,
  ConnectClientLinkedService,
  Course,
  CourseStreamEvent,
  LinkedService,
  LinkedServiceUser,
  LiveClass,
  LiveClassSession,
  ProfileImage,
  Quiz,
  SpecialCertification,
  Template,
  TimeStamps,
  TitleScreen,
  User,
  UserAccessLevel,
  UserSubscription,
  Video,
} from '../core/types'

const strEq = eqBy(JSON.stringify)

export type AccessLevel = {
  id: string
  name: string
}
type AccessLevelUser = AccessLevel & { userID: string }
type AccessLevelLinkedService = AccessLevel & { linkedServiceID: string }

type AccessLevelChartRow = {
  id: number
  label: string
  value: number
}

type UserRole = {
  roleID: string
  roleName: string
  userID: string
}

type Role = {
  id: string
  name: string
} & TimeStamps

type AccessLevelChart = AccessLevelChartRow[]

export type QuizResult = {
  id: string
  result: string
  score: number
  quizID: string
  userID: string
  classID: string
} & TimeStamps

export type LiveClassCourse = {
  id: string
  code: string
  courseLength: number
  courseName: string
  templateID: string
} & TimeStamps

export type LiveClassSurvey = {
  userID: string
  className: string
  quizCompletedTime: string
  passOrFail: 'pass' | 'fail'
  score: number
  surveyUrl: string
}

export type CourseSurvey = {
  userID: string
  courseName: string
  quizCompletedTime: string
  passOrFail: 'pass' | 'fail'
  score: number
  surveyUrl: string
}

type OutstandingExport = {
  id: string
  emsID: string
  status: string
  exportStatus: string
  completionDate: string
  courseCode: string
}

type Export = {
  count: number
  timestamp: string
}

export let fetchUsers = createAsyncThunk('admin/fetchUsers', (query: string) =>
  axios.get<User[]>(`/api/v2/admin/users?query=${query}`).then(pluckData),
)

export let fetchCourses = createAsyncThunk('admin/fetchCourses', () =>
  axios.get<Course[]>(`/api/v2/admin/courses`).then(pluckData),
)

export let fetchProfileImages = createAsyncThunk(
  'admin/fetchProfileImages',
  (userIDs: string[]) =>
    axios
      .get<ProfileImage[]>(
        `/api/v2/admin/users/profile-images?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchLinkedServices = createAsyncThunk(
  'admin/fetchLinkedServices',
  () =>
    axios.get<LinkedService[]>('/api/v2/admin/linked-services').then(pluckData),
)

export let fetchLinkedServiceUsers = createAsyncThunk(
  'admin/fetchLinkedServiceUsers',
  (userIDs: string[]) =>
    axios
      .get<LinkedServiceUser[]>(
        `/api/v2/admin/linked-service-users?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchLinkedServiceRoster = createAsyncThunk(
  'admin/fetchLinkedServiceRoster',
  (linkedServiceID: string) =>
    axios
      .get<LinkedServiceUser[]>(
        `/api/v2/admin/linked-services/roster?linkedServiceID=${linkedServiceID}`,
      )
      .then(pluckData),
)

export let fetchConnectClients = createAsyncThunk(
  'admin/fetchConnectClients',
  () =>
    axios.get<ConnectClient[]>('/api/v2/admin/connect-clients').then(pluckData),
)

export let fetchConnectClient = createAsyncThunk(
  'admin/fetchConnectClient',
  (id: string) =>
    axios
      .get<ConnectClient>(`/api/v2/admin/connect-clients/${id}`)
      .then(pluckData),
)

export let createConnectClient = createAsyncThunk(
  'admin/createConnectClient',
  (data: { clientName: string; clientID: string }) =>
    axios
      .post<ConnectClient>('/api/v2/admin/connect-clients', data)
      .then(pluckData),
)

export let updateConnectClient = createAsyncThunk(
  'admin/updateConnectClient',
  ({
    id,
    data,
  }: {
    id: string
    data: { clientName: string; clientID: string }
  }) =>
    axios
      .put<ConnectClient>(`/api/v2/admin/connect-clients/${id}`, data)
      .then(pluckData),
)

export let deleteConnectClient = createAsyncThunk(
  'admin/deleteConnectClient',
  (id: string) =>
    axios.delete(`/api/v2/admin/connect-clients/${id}`).then(pluckData),
)

export let fetchConnectClientLinkedServices = createAsyncThunk(
  'admin/fetchConnectClientLinkedServices',
  (id: string) =>
    axios
      .get<ConnectClientLinkedService[]>(
        `/api/v2/admin/connect-clients/${id}/linked-services`,
      )
      .then(pluckData),
)

export let addConnectClientLinkedService = createAsyncThunk(
  'admin/addConnectClientLinkedService',
  ({ id, linkedServiceID }: { id: string; linkedServiceID: string }) =>
    axios
      .post<ConnectClientLinkedService>(
        `/api/v2/admin/connect-clients/${id}/linked-services`,
        { linkedServiceID },
      )
      .then(pluckData),
)

export let removeConnectClientLinkedService = createAsyncThunk(
  'admin/removeConnectClientLinkedService',
  ({ id, linkedServiceId }: { id: string; linkedServiceId: string }) =>
    axios
      .delete(
        `/api/v2/admin/connect-clients/${id}/linked-services/${linkedServiceId}`,
      )
      .then(pluckData),
)

export let fetchRoles = createAsyncThunk('admin/fetchRoles', () =>
  axios.get<Role[]>(`/api/v2/admin/roles`).then(pluckData),
)

export let fetchUserRoles = createAsyncThunk(
  'admin/fetchUserRoles',
  (userIDs: string[]) =>
    axios
      .get<UserRole[]>(`/api/v2/admin/user-roles?userIDs=${userIDs.join(',')}`)
      .then(pluckData),
)

export let fetchAccessLevelUsers = createAsyncThunk(
  'admin/fetchAccessLevelUsers',
  () =>
    axios
      .get<AccessLevelUser[]>('/api/v2/admin/access-level/user-list')
      .then(pluckData),
)

export let fetchAccessLevelLinkedServices = createAsyncThunk(
  'admin/fetchAccessLevelLinkedServices',
  () =>
    axios
      .get<AccessLevelLinkedService[]>(
        '/api/v2/admin/access-level/linked-services',
      )
      .then(pluckData),
)

export let fetchAccessLevels = createAsyncThunk('admin/fetchAccessLevels', () =>
  axios.get<AccessLevel[]>('/api/v2/admin/access-levels').then(pluckData),
)

export let fetchAccessLevelUsersCountChart = createAsyncThunk(
  'admin/fetchAccessLevelUsersCountChart',
  () =>
    axios
      .get<AccessLevelChart>('/api/v2/admin/access-level/users/counts/chart')
      .then(pluckData),
)

export let fetchAccessLevelLinkedServicesCountChart = createAsyncThunk(
  'admin/fetchAccessLevelLinkedServicesCountChart',
  () =>
    axios
      .get<AccessLevelChart>(
        '/api/v2/admin/access-level/linked-services/counts/chart',
      )
      .then(pluckData),
)

export let fetchUserCertificates = createAsyncThunk(
  'admin/fetchUserCertificates',
  (userIDs: string[]) =>
    axios
      .get<Certificate[]>(
        `/api/v2/admin/user/certificates?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchUserClassEvents = createAsyncThunk(
  'admin/fetchUserClassEvents',
  (userIDs: string[]) =>
    axios
      .get<ClassStreamEvent[]>(
        `/api/v2/admin/stream-event/class?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchUserCourseEvents = createAsyncThunk(
  'admin/fetchUserCourseEvents',
  (userIDs: string[]) =>
    axios
      .get<CourseStreamEvent[]>(
        `/api/v2/admin/stream-event/course?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchUserQuizResults = createAsyncThunk(
  'admin/fetchUserQuizResults',
  (userIDs: string[]) =>
    axios
      .get<QuizResult[]>(
        `/api/v2/admin/user/quiz-results?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchClasses = createAsyncThunk('admin/fetchClasses', () =>
  axios.get<Class[]>('/api/v2/admin/classes').then(pluckData),
)

// Course code is appended on Admin v3's GET for live classes for convenience
// But shouldn't be relied upon. Consider it metadata.
type LiveClassWithCourseCode = LiveClass & { courseCode?: string }

export let fetchLiveClasses = createAsyncThunk('admin/fetchLiveClasses', () =>
  axios
    .get<LiveClassWithCourseCode[]>('/api/v2/admin/live-classes')
    .then(pluckData),
)

export let fetchLiveClassSessions = createAsyncThunk(
  'admin/fetchLiveClassSessions',
  () =>
    axios
      .get<LiveClassSession[]>('/api/v2/admin/live-class/sessions')
      .then(pluckData),
)

export let fetchLiveClassTemplates = createAsyncThunk(
  'admin/fetchLiveClassTemplates',
  () =>
    axios.get<Template[]>('/api/v2/admin/live-class/template').then(pluckData),
)

export let fetchLiveClassAccreditations = createAsyncThunk(
  'admin/fetchLiveClassAccreditations',
  () =>
    axios
      .get<Accreditation[]>('/api/v2/admin/live-class/accreditations')
      .then(pluckData),
)

export let fetchLiveClassCourses = createAsyncThunk(
  'admin/fetchLiveClassCourses',
  () =>
    axios
      .get<LiveClassCourse[]>('/api/v2/admin/live-class/courses')
      .then(pluckData),
)

export let fetchLiveClassTitleScreens = createAsyncThunk(
  'admin/fetchLiveClassTitleScreens',
  (id: string) =>
    axios
      .get<TitleScreen>(
        `/api/v2/admin/live-class/title-screen?titleScreenID=${id}`,
      )
      .then(pluckData),
)

export let fetchLiveClassQuizzes = createAsyncThunk(
  'admin/fetchLiveClassQuizzes',
  (id: string) =>
    axios.get<Quiz>(`/api/v2/live-class/quiz?classID=${id}`).then(pluckData),
)

export let fetchUserSubscriptions = createAsyncThunk(
  'admin/fetchUserSubscriptions',
  (userIDs: string[]) =>
    axios
      .get<UserSubscription[]>(
        `/api/v2/admin/subscriptions?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchUserAccessLevels = createAsyncThunk(
  'admin/fetchUserAccessLevels',
  (userIDs: string[]) =>
    axios
      .get<UserAccessLevel[]>(
        `/api/v2/admin/access-level/users?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchSpecialCertifications = createAsyncThunk(
  'admin/fetchSpecialCertifications',
  () =>
    axios
      .get<SpecialCertification[]>(`/api/v2/admin/special-certification`)
      .then(pluckData),
)

export let fetchAllVideos = createAsyncThunk('admin/fetchAllVideos', () =>
  axios.get<Video[]>(`/api/v2/admin/videos`).then(pluckData),
)

export let fetchLiveClassSurveys = createAsyncThunk(
  'admin/fetchLiveClassSurveys',
  (userIDs: string[]) =>
    axios
      .get<LiveClassSurvey[]>(
        `/api/v2/admin/survey/live/class/surveys?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchCourseSurveys = createAsyncThunk(
  'admin/fetchCourseSurveys',
  (userIDs: string[]) =>
    axios
      .get<CourseSurvey[]>(
        `/api/v2/admin/survey/course/surveys?userIDs=${userIDs.join(',')}`,
      )
      .then(pluckData),
)

export let fetchExports = createAsyncThunk('admin/fetchExports', () =>
  axios.get<Export[]>(`/api/v2/admin/accreditations/exports`).then(pluckData),
)

export let fetchOutstandingExports = createAsyncThunk(
  'admin/fetchOutstandingExports',
  () =>
    axios
      .get<OutstandingExport[]>(
        `/api/v2/admin/accreditations/outstanding-exports`,
      )
      .then(pluckData),
)

type DashboardChartExpanded =
  'None' | 'AccessLevelUsers' | 'AccessLevelLinkedServices'

type AdminState = {
  linkedServicesStatus: Status
  linkedServices: LinkedService[]
  connectClientsStatus: Status
  connectClients: ConnectClient[]
  selectedConnectClient: string
  connectClientMappingsStatus: Record<string, Status>
  connectClientMappings: Record<string, ConnectClientLinkedService[]>
  accessLevelsStatus: Status
  accessLevels: AccessLevel[]
  accessLevelUsersStatus: Status
  accessLevelUsers: AccessLevelUser[]
  accessLevelLinkedServicesStatus: Status
  accessLevelLinkedServices: AccessLevelLinkedService[]
  accessLevelUsersCountChartStatus: Status
  accessLevelUsersCountChart: AccessLevelChart
  accessLevelLinkedServicesCountChartStatus: Status
  accessLevelLinkedServicesCountChart: AccessLevelChart
  dashboardChartExpanded: DashboardChartExpanded
  errorMessage: string
  successMessage: string
  selectedLinkedService: string
  usersStatus: Status
  users: User[]
  profileImagesStatus: Status
  profileImages: ProfileImage[]
  linkedServiceUsersStatus: Status
  linkedServiceUsers: LinkedServiceUser[]
  linkedServiceRosterStatus: Record<string, Status>
  linkedServiceRoster: Record<string, LinkedServiceUser[]>
  userRolesStatus: Status
  userRoles: UserRole[]
  rolesStatus: Status
  roles: Role[]
  userCertificatesStatus: Status
  userCertificates: Certificate[]
  classesStatus: Status
  classes: Class[]
  coursesStatus: Status
  courses: Course[]
  classEventsStatus: Status
  classEvents: ClassStreamEvent[]
  courseEventsStatus: Status
  courseEvents: CourseStreamEvent[]
  quizResultsStatus: Status
  quizResults: QuizResult[]
  allVideos: Video[]
  allVideosStatus: Status
  liveClassesStatus: Status
  liveClasses: LiveClassWithCourseCode[]
  liveClassSessionsStatus: Status
  liveClassSessions: LiveClassSession[]
  liveClassTemplatesStatus: Status
  liveClassTemplates: Template[]
  liveClassAccreditationsStatus: Status
  liveClassAccreditations: Accreditation[]
  liveClassCoursesStatus: Status
  liveClassCourses: LiveClassCourse[]
  liveClassTitleScreensStatus: Record<string, Status>
  liveClassTitleScreens: TitleScreen[]
  liveClassQuizzesStatus: Status
  liveClassQuizzes: Record<string, Quiz>
  liveClassSurveysStatus: Status
  liveClassSurveys: LiveClassSurvey[]
  courseSurveysStatus: Status
  courseSurveys: CourseSurvey[]
  subscriptionsStatus: Status
  subscriptions: UserSubscription[]
  userAccessLevelStatus: Status
  userAccessLevels: UserAccessLevel[]
  specialCertificationsStatus: Status
  specialCertifications: SpecialCertification[]
  exports: Export[]
  exportsStatus: Status
  outstandingExports: OutstandingExport[]
  outstandingExportsStatus: Status
}

export let initialState: AdminState = {
  linkedServicesStatus: 'uninitialized',
  linkedServices: [],
  connectClientsStatus: 'uninitialized',
  connectClients: [],
  selectedConnectClient: '',
  connectClientMappingsStatus: {},
  connectClientMappings: {},
  accessLevelsStatus: 'uninitialized',
  accessLevels: [],
  accessLevelUsersStatus: 'uninitialized',
  accessLevelUsers: [],
  accessLevelLinkedServicesStatus: 'uninitialized',
  accessLevelLinkedServices: [],
  accessLevelUsersCountChartStatus: 'uninitialized',
  accessLevelUsersCountChart: [],
  accessLevelLinkedServicesCountChartStatus: 'uninitialized',
  accessLevelLinkedServicesCountChart: [],
  dashboardChartExpanded: 'None',
  errorMessage: '',
  successMessage: '',
  selectedLinkedService: '',
  usersStatus: 'uninitialized',
  users: [],
  profileImagesStatus: 'uninitialized',
  profileImages: [],
  linkedServiceUsersStatus: 'uninitialized',
  linkedServiceUsers: [],
  linkedServiceRosterStatus: {},
  linkedServiceRoster: {},
  userRolesStatus: 'uninitialized',
  userRoles: [],
  rolesStatus: 'uninitialized',
  roles: [],
  userCertificatesStatus: 'uninitialized',
  userCertificates: [],
  classesStatus: 'uninitialized',
  classes: [],
  coursesStatus: 'uninitialized',
  courses: [],
  classEventsStatus: 'uninitialized',
  classEvents: [],
  courseEventsStatus: 'uninitialized',
  courseEvents: [],
  quizResultsStatus: 'uninitialized',
  quizResults: [],
  allVideos: [],
  allVideosStatus: 'uninitialized',
  liveClassesStatus: 'uninitialized',
  liveClasses: [],
  liveClassSessionsStatus: 'uninitialized',
  liveClassSessions: [],
  liveClassTemplatesStatus: 'uninitialized',
  liveClassTemplates: [],
  liveClassAccreditationsStatus: 'uninitialized',
  liveClassAccreditations: [],
  liveClassCoursesStatus: 'uninitialized',
  liveClassCourses: [],
  liveClassTitleScreensStatus: {},
  liveClassTitleScreens: [],
  liveClassQuizzesStatus: 'uninitialized',
  liveClassQuizzes: {},
  liveClassSurveysStatus: 'uninitialized',
  liveClassSurveys: [],
  courseSurveysStatus: 'uninitialized',
  courseSurveys: [],
  subscriptionsStatus: 'uninitialized',
  subscriptions: [],
  userAccessLevelStatus: 'uninitialized',
  userAccessLevels: [],
  specialCertificationsStatus: 'uninitialized',
  specialCertifications: [],
  exports: [],
  exportsStatus: 'uninitialized',
  outstandingExports: [],
  outstandingExportsStatus: 'uninitialized',
}

export let adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    linkedServicesChanged: (state, action: PayloadAction<LinkedService[]>) => {
      state.linkedServices = action.payload
    },
    linkedServicesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.linkedServicesStatus = action.payload
    },
    accessLevelsChanged: (state, action: PayloadAction<AccessLevel[]>) => {
      state.accessLevels = action.payload
    },
    accessLevelsStatusChanged: (state, action: PayloadAction<Status>) => {
      state.accessLevelsStatus = action.payload
    },
    accessLevelUsersChanged: (
      state,
      action: PayloadAction<AccessLevelUser[]>,
    ) => {
      state.accessLevelUsers = action.payload
    },
    accessLevelUsersStatusChanged: (state, action: PayloadAction<Status>) => {
      state.accessLevelUsersStatus = action.payload
    },
    accessLevelLinkedServicesChanged: (
      state,
      action: PayloadAction<AccessLevelLinkedService[]>,
    ) => {
      state.accessLevelLinkedServices = action.payload
    },
    accessLevelLinkedServicesStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.accessLevelLinkedServicesStatus = action.payload
    },
    accessLevelUsersCountChartChanged: (
      state,
      action: PayloadAction<AccessLevelChart>,
    ) => {
      state.accessLevelUsersCountChart = action.payload
    },
    accessLevelUsersCountChartStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.accessLevelUsersCountChartStatus = action.payload
    },
    accessLevelLinkedServicesChartChanged: (
      state,
      action: PayloadAction<AccessLevelChart>,
    ) => {
      state.accessLevelLinkedServicesCountChart = action.payload
    },
    accessLevelLinkedServicesChartStatusChanged: (
      state,
      action: PayloadAction<Status>,
    ) => {
      state.accessLevelLinkedServicesCountChartStatus = action.payload
    },
    dashboardChartExpandedChanged: (
      state,
      action: PayloadAction<DashboardChartExpanded>,
    ) => {
      state.dashboardChartExpanded = action.payload
    },
    errorMessageChanged: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    successMessageChanged: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload
    },
    selectedLinkedServiceChanged: (state, action: PayloadAction<string>) => {
      state.selectedLinkedService = action.payload
    },
    selectedConnectClientChanged: (state, action: PayloadAction<string>) => {
      state.selectedConnectClient = action.payload
    },
    usersChanged: (state, action: PayloadAction<User[]>) => {
      state.users = uniqBy((x) => x.id, concat(state.users, action.payload))
    },
    resetUsers: (state) => {
      state.users = []
    },
    usersStatusChanged: (state, action: PayloadAction<Status>) => {
      state.usersStatus = action.payload
    },
    profilImagesChanged: (state, action: PayloadAction<ProfileImage[]>) => {
      state.profileImages = uniqBy(
        (x) => x.id,
        concat(state.profileImages, action.payload),
      )
    },
    profilImagesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.profileImagesStatus = action.payload
    },
    linkedServiceUsersChanged: (
      state,
      action: PayloadAction<LinkedServiceUser[]>,
    ) => {
      state.linkedServiceUsers = uniqBy(
        (x) => x.id,
        concat(state.linkedServiceUsers, action.payload),
      )
    },
    linkedServiceUserRemoved: (
      state,
      action: PayloadAction<[userID: string, LinkedServiceID: string]>,
    ) => {
      let userToRemove = state.linkedServiceUsers.find(
        (x) =>
          x.userID === action.payload[0] &&
          x.linkedServiceID === action.payload[1],
      )
      if (userToRemove) {
        state.linkedServiceUsers = without(
          [userToRemove],
          state.linkedServiceUsers,
        )
      }
    },
    linkedServiceUsersStatusChanged: (state, action: PayloadAction<Status>) => {
      state.linkedServiceUsersStatus = action.payload
    },
    userRolesChanged: (state, action: PayloadAction<UserRole[]>) => {
      state.userRoles = uniqWith(strEq, concat(state.userRoles, action.payload))
    },
    userRolesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.userRolesStatus = action.payload
    },
    userCertificatesChanged: (state, action: PayloadAction<Certificate[]>) => {
      state.userCertificates = uniqBy(
        (x) => x.id,
        concat(state.userCertificates, action.payload),
      )
    },
    userCerificatesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.userCertificatesStatus = action.payload
    },
    classesChanged: (state, action: PayloadAction<Class[]>) => {
      state.classes = action.payload
    },
    classesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.classesStatus = action.payload
    },
    coursesChanged: (state, action: PayloadAction<Course[]>) => {
      state.courses = action.payload
    },
    coursesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.coursesStatus = action.payload
    },
    allVideosChanged: (state, action: PayloadAction<Video[]>) => {
      state.allVideos = action.payload
    },
    allVideosStatusChanged: (state, action: PayloadAction<Status>) => {
      state.allVideosStatus = action.payload
    },
    userClassEventsChanged: (
      state,
      action: PayloadAction<ClassStreamEvent[]>,
    ) => {
      state.classEvents = uniqBy(
        (x) => x.id,
        concat(state.classEvents, action.payload),
      )
    },
    userClassEventsStatusChanged: (state, action: PayloadAction<Status>) => {
      state.classEventsStatus = action.payload
    },
    userCourseEventsStatusChanged: (state, action: PayloadAction<Status>) => {
      state.courseEventsStatus = action.payload
    },
    userCourseEventsChanged: (
      state,
      action: PayloadAction<CourseStreamEvent[]>,
    ) => {
      state.courseEvents = uniqBy(
        (x) => x.id,
        concat(state.courseEvents, action.payload),
      )
    },
    userQuizResultsChanged: (state, action: PayloadAction<QuizResult[]>) => {
      state.quizResults = uniqBy(
        (x) => x.id,
        concat(state.quizResults, action.payload),
      )
    },
    userQuizResultsStatusChanged: (state, action: PayloadAction<Status>) => {
      state.quizResultsStatus = action.payload
    },
    liveClassesStatusChanged: (state, action: PayloadAction<Status>) => {
      state.liveClassesStatus = action.payload
    },
    liveClassesChanged: (
      state,
      action: PayloadAction<LiveClassWithCourseCode[]>,
    ) => {
      state.liveClasses = action.payload
    },
    liveClassSessionsStatusChanged: (state, action: PayloadAction<Status>) => {
      state.liveClassSessionsStatus = action.payload
    },
    liveClassSessionsChanged: (
      state,
      action: PayloadAction<LiveClassSession[]>,
    ) => {
      state.liveClassSessions = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLinkedServices.pending, (state) => {
        state.linkedServicesStatus = 'pending'
      })
      .addCase(fetchLinkedServices.rejected, (state) => {
        state.linkedServicesStatus = 'rejected'
      })
      .addCase(fetchLinkedServices.fulfilled, (state, action) => {
        state.linkedServicesStatus = 'fulfilled'
        state.linkedServices = action.payload
      })
      .addCase(fetchConnectClients.pending, (state) => {
        state.connectClientsStatus = 'pending'
      })
      .addCase(fetchConnectClients.rejected, (state) => {
        state.connectClientsStatus = 'rejected'
      })
      .addCase(fetchConnectClients.fulfilled, (state, action) => {
        state.connectClientsStatus = 'fulfilled'
        state.connectClients = action.payload
      })
      .addCase(createConnectClient.fulfilled, (state, action) => {
        state.connectClients = [...state.connectClients, action.payload]
      })
      .addCase(updateConnectClient.fulfilled, (state, action) => {
        state.connectClients = state.connectClients.map((client) =>
          client.id === action.payload.id ? action.payload : client,
        )
      })
      .addCase(deleteConnectClient.fulfilled, (state, action) => {
        state.connectClients = state.connectClients.filter(
          (client) => client.id !== action.meta.arg,
        )
      })
      .addCase(fetchConnectClientLinkedServices.pending, (state, action) => {
        state.connectClientMappingsStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchConnectClientLinkedServices.rejected, (state, action) => {
        state.connectClientMappingsStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchConnectClientLinkedServices.fulfilled, (state, action) => {
        state.connectClientMappingsStatus[action.meta.arg] = 'fulfilled'
        state.connectClientMappings[action.meta.arg] = action.payload
      })
      .addCase(addConnectClientLinkedService.fulfilled, (state, action) => {
        let clientID = action.meta.arg.id
        if (state.connectClientMappings[clientID]) {
          state.connectClientMappings[clientID] = [
            ...state.connectClientMappings[clientID],
            action.payload,
          ]
        }
      })
      .addCase(removeConnectClientLinkedService.fulfilled, (state, action) => {
        let { id: clientID, linkedServiceId } = action.meta.arg
        if (state.connectClientMappings[clientID]) {
          state.connectClientMappings[clientID] = state.connectClientMappings[
            clientID
          ].filter((mapping) => mapping.linkedServiceID !== linkedServiceId)
        }
      })
      .addCase(fetchAccessLevels.pending, (state) => {
        state.accessLevelsStatus = 'pending'
      })
      .addCase(fetchAccessLevels.rejected, (state) => {
        state.accessLevelsStatus = 'rejected'
      })
      .addCase(fetchAccessLevels.fulfilled, (state, action) => {
        state.accessLevelsStatus = 'fulfilled'
        state.accessLevels = action.payload
      })
      .addCase(fetchAccessLevelUsers.pending, (state) => {
        state.accessLevelUsersStatus = 'pending'
      })
      .addCase(fetchAccessLevelUsers.rejected, (state) => {
        state.accessLevelUsersStatus = 'rejected'
      })
      .addCase(fetchAccessLevelUsers.fulfilled, (state, action) => {
        state.accessLevelUsersStatus = 'fulfilled'
        state.accessLevelUsers = action.payload
      })
      .addCase(fetchAccessLevelLinkedServices.pending, (state) => {
        state.accessLevelLinkedServicesStatus = 'pending'
      })
      .addCase(fetchAccessLevelLinkedServices.rejected, (state) => {
        state.accessLevelLinkedServicesStatus = 'rejected'
      })
      .addCase(fetchAccessLevelLinkedServices.fulfilled, (state, action) => {
        state.accessLevelLinkedServicesStatus = 'fulfilled'
        state.accessLevelLinkedServices = action.payload
      })
      .addCase(fetchAccessLevelUsersCountChart.pending, (state) => {
        state.accessLevelUsersCountChartStatus = 'pending'
      })
      .addCase(fetchAccessLevelUsersCountChart.rejected, (state) => {
        state.accessLevelUsersCountChartStatus = 'rejected'
      })
      .addCase(fetchAccessLevelUsersCountChart.fulfilled, (state, action) => {
        state.accessLevelUsersCountChartStatus = 'fulfilled'
        state.accessLevelUsersCountChart = action.payload
      })
      .addCase(fetchAccessLevelLinkedServicesCountChart.pending, (state) => {
        state.accessLevelLinkedServicesCountChartStatus = 'pending'
      })
      .addCase(fetchAccessLevelLinkedServicesCountChart.rejected, (state) => {
        state.accessLevelLinkedServicesCountChartStatus = 'rejected'
      })
      .addCase(
        fetchAccessLevelLinkedServicesCountChart.fulfilled,
        (state, action) => {
          state.accessLevelLinkedServicesCountChartStatus = 'fulfilled'
          state.accessLevelLinkedServicesCountChart = action.payload
        },
      )
      .addCase(fetchUsers.pending, (state) => {
        state.usersStatus = 'pending'
      })
      .addCase(fetchUsers.rejected, (state) => {
        state.usersStatus = 'rejected'
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersStatus = 'fulfilled'
        state.users = uniqBy((x) => x.id, concat(action.payload, state.users))
      })
      .addCase(fetchProfileImages.pending, (state) => {
        state.profileImagesStatus = 'pending'
      })
      .addCase(fetchProfileImages.rejected, (state) => {
        state.profileImagesStatus = 'rejected'
      })
      .addCase(fetchProfileImages.fulfilled, (state, action) => {
        state.profileImagesStatus = 'fulfilled'
        state.profileImages = uniqBy(
          (x) => x.id,
          concat(state.profileImages, action.payload),
        )
      })
      .addCase(fetchLinkedServiceUsers.pending, (state) => {
        state.linkedServiceUsersStatus = 'pending'
      })
      .addCase(fetchLinkedServiceUsers.rejected, (state) => {
        state.linkedServiceUsersStatus = 'rejected'
      })
      .addCase(fetchLinkedServiceUsers.fulfilled, (state, action) => {
        state.linkedServiceUsersStatus = 'fulfilled'
        state.linkedServiceUsers = uniqBy(
          (x) => x.id,
          concat(state.linkedServiceUsers, action.payload),
        )
      })
      .addCase(fetchLinkedServiceRoster.pending, (state, action) => {
        state.linkedServiceRosterStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchLinkedServiceRoster.rejected, (state, action) => {
        state.linkedServiceRosterStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchLinkedServiceRoster.fulfilled, (state, action) => {
        state.linkedServiceRosterStatus[action.meta.arg] = 'fulfilled'
        state.linkedServiceRoster[action.meta.arg] = action.payload
      })
      .addCase(fetchUserRoles.pending, (state) => {
        state.userRolesStatus = 'pending'
      })
      .addCase(fetchUserRoles.rejected, (state) => {
        state.userRolesStatus = 'rejected'
      })
      .addCase(fetchUserRoles.fulfilled, (state, action) => {
        state.userRolesStatus = 'fulfilled'
        state.userRoles = [
          ...action.payload,
          ...state.userRoles.filter(
            (ur) => !action.meta.arg.includes(ur.userID),
          ),
        ]
      })
      .addCase(fetchRoles.pending, (state) => {
        state.rolesStatus = 'pending'
      })
      .addCase(fetchRoles.rejected, (state) => {
        state.rolesStatus = 'rejected'
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.rolesStatus = 'fulfilled'
        state.roles = action.payload
      })
      .addCase(fetchUserCertificates.pending, (state) => {
        state.userCertificatesStatus = 'pending'
      })
      .addCase(fetchUserCertificates.rejected, (state) => {
        state.userCertificatesStatus = 'rejected'
      })
      .addCase(fetchUserCertificates.fulfilled, (state, action) => {
        state.userCertificatesStatus = 'fulfilled'
        state.userCertificates = uniqBy(
          (x) => x.id,
          concat(state.userCertificates, action.payload),
        )
      })
      .addCase(fetchClasses.pending, (state) => {
        state.classesStatus = 'pending'
      })
      .addCase(fetchClasses.rejected, (state) => {
        state.classesStatus = 'rejected'
      })
      .addCase(fetchClasses.fulfilled, (state, action) => {
        state.classesStatus = 'fulfilled'
        state.classes = action.payload
      })
      .addCase(fetchCourses.pending, (state) => {
        state.coursesStatus = 'pending'
      })
      .addCase(fetchCourses.rejected, (state) => {
        state.coursesStatus = 'rejected'
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.coursesStatus = 'fulfilled'
        state.courses = action.payload
      })
      .addCase(fetchUserClassEvents.pending, (state) => {
        state.classEventsStatus = 'pending'
      })
      .addCase(fetchUserClassEvents.rejected, (state) => {
        state.classEventsStatus = 'rejected'
      })
      .addCase(fetchUserClassEvents.fulfilled, (state, action) => {
        state.classEventsStatus = 'fulfilled'
        state.classEvents = uniqBy(
          (x) => x.id,
          concat(state.classEvents, action.payload),
        )
      })
      .addCase(fetchUserCourseEvents.pending, (state) => {
        state.courseEventsStatus = 'pending'
      })
      .addCase(fetchUserCourseEvents.rejected, (state) => {
        state.courseEventsStatus = 'rejected'
      })
      .addCase(fetchUserCourseEvents.fulfilled, (state, action) => {
        state.courseEventsStatus = 'fulfilled'
        state.courseEvents = uniqBy(
          (x) => x.id,
          concat(state.courseEvents, action.payload),
        )
      })
      .addCase(fetchUserQuizResults.pending, (state) => {
        state.quizResultsStatus = 'pending'
      })
      .addCase(fetchUserQuizResults.rejected, (state) => {
        state.quizResultsStatus = 'rejected'
      })
      .addCase(fetchUserQuizResults.fulfilled, (state, action) => {
        state.quizResultsStatus = 'fulfilled'
        state.quizResults = uniqBy(
          (x) => x.id,
          concat(state.quizResults, action.payload),
        )
      })
      .addCase(fetchAllVideos.pending, (state) => {
        state.allVideosStatus = 'pending'
      })
      .addCase(fetchAllVideos.rejected, (state) => {
        state.allVideosStatus = 'rejected'
      })
      .addCase(fetchAllVideos.fulfilled, (state, action) => {
        state.allVideosStatus = 'fulfilled'
        state.allVideos = action.payload
      })
      .addCase(fetchLiveClasses.pending, (state) => {
        state.liveClassesStatus = 'pending'
      })
      .addCase(fetchLiveClasses.rejected, (state) => {
        state.liveClassesStatus = 'rejected'
      })
      .addCase(fetchLiveClasses.fulfilled, (state, action) => {
        state.liveClassesStatus = 'fulfilled'
        state.liveClasses = action.payload
      })
      .addCase(fetchLiveClassSessions.pending, (state) => {
        state.liveClassSessionsStatus = 'pending'
      })
      .addCase(fetchLiveClassSessions.rejected, (state) => {
        state.liveClassSessionsStatus = 'rejected'
      })
      .addCase(fetchLiveClassSessions.fulfilled, (state, action) => {
        state.liveClassSessionsStatus = 'fulfilled'
        state.liveClassSessions = action.payload
      })
      .addCase(fetchLiveClassTemplates.pending, (state) => {
        state.liveClassTemplatesStatus = 'pending'
      })
      .addCase(fetchLiveClassTemplates.rejected, (state) => {
        state.liveClassTemplatesStatus = 'rejected'
      })
      .addCase(fetchLiveClassTemplates.fulfilled, (state, action) => {
        state.liveClassTemplatesStatus = 'fulfilled'
        state.liveClassTemplates = action.payload
      })
      .addCase(fetchLiveClassAccreditations.pending, (state) => {
        state.liveClassAccreditationsStatus = 'pending'
      })
      .addCase(fetchLiveClassAccreditations.rejected, (state) => {
        state.liveClassAccreditationsStatus = 'rejected'
      })
      .addCase(fetchLiveClassAccreditations.fulfilled, (state, action) => {
        state.liveClassAccreditationsStatus = 'fulfilled'
        state.liveClassAccreditations = action.payload
      })
      .addCase(fetchLiveClassCourses.pending, (state) => {
        state.liveClassCoursesStatus = 'pending'
      })
      .addCase(fetchLiveClassCourses.rejected, (state) => {
        state.liveClassCoursesStatus = 'rejected'
      })
      .addCase(fetchLiveClassCourses.fulfilled, (state, action) => {
        state.liveClassCoursesStatus = 'fulfilled'
        state.liveClassCourses = action.payload
      })
      .addCase(fetchLiveClassTitleScreens.pending, (state, action) => {
        state.liveClassTitleScreensStatus[action.meta.arg] = 'pending'
      })
      .addCase(fetchLiveClassTitleScreens.rejected, (state, action) => {
        state.liveClassTitleScreensStatus[action.meta.arg] = 'rejected'
      })
      .addCase(fetchLiveClassTitleScreens.fulfilled, (state, action) => {
        state.liveClassTitleScreensStatus[action.meta.arg] = 'fulfilled'
        state.liveClassTitleScreens = [
          ...state.liveClassTitleScreens.filter(
            (ts) => ts.id !== action.payload.id,
          ),
          action.payload,
        ]
      })
      .addCase(fetchLiveClassQuizzes.pending, (state) => {
        state.liveClassQuizzesStatus = 'pending'
      })
      .addCase(fetchLiveClassQuizzes.rejected, (state) => {
        state.liveClassQuizzesStatus = 'rejected'
      })
      .addCase(fetchLiveClassQuizzes.fulfilled, (state, action) => {
        state.liveClassQuizzesStatus = 'fulfilled'
        state.liveClassQuizzes[action.meta.arg] = action.payload
      })
      .addCase(fetchLiveClassSurveys.pending, (state) => {
        state.liveClassSurveysStatus = 'pending'
      })
      .addCase(fetchLiveClassSurveys.rejected, (state) => {
        state.liveClassSurveysStatus = 'rejected'
      })
      .addCase(fetchLiveClassSurveys.fulfilled, (state, action) => {
        state.liveClassSurveysStatus = 'fulfilled'
        state.liveClassSurveys = action.payload
      })
      .addCase(fetchCourseSurveys.pending, (state) => {
        state.courseSurveysStatus = 'pending'
      })
      .addCase(fetchCourseSurveys.rejected, (state) => {
        state.courseSurveysStatus = 'rejected'
      })
      .addCase(fetchCourseSurveys.fulfilled, (state, action) => {
        state.courseSurveysStatus = 'fulfilled'
        state.courseSurveys = action.payload
      })
      .addCase(fetchUserSubscriptions.pending, (state) => {
        state.subscriptionsStatus = 'pending'
      })
      .addCase(fetchUserSubscriptions.rejected, (state) => {
        state.subscriptionsStatus = 'rejected'
      })
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        state.subscriptionsStatus = 'fulfilled'
        state.subscriptions = [
          ...action.payload,
          ...state.subscriptions.filter(
            (s) => !action.meta.arg.some((userID) => userID === s.userID),
          ),
        ]
      })
      .addCase(fetchUserAccessLevels.pending, (state) => {
        state.userAccessLevelStatus = 'pending'
      })
      .addCase(fetchUserAccessLevels.rejected, (state) => {
        state.userAccessLevelStatus = 'rejected'
      })
      .addCase(fetchUserAccessLevels.fulfilled, (state, action) => {
        state.userAccessLevelStatus = 'fulfilled'
        state.userAccessLevels = [
          ...action.payload,
          ...state.userAccessLevels.filter(
            (ur) => !action.meta.arg.some((userID) => userID === ur.userID),
          ),
        ]
      })
      .addCase(fetchSpecialCertifications.pending, (state) => {
        state.specialCertificationsStatus = 'pending'
      })
      .addCase(fetchSpecialCertifications.rejected, (state) => {
        state.specialCertificationsStatus = 'rejected'
      })
      .addCase(fetchSpecialCertifications.fulfilled, (state, action) => {
        state.specialCertificationsStatus = 'fulfilled'
        state.specialCertifications = action.payload
      })
      .addCase(fetchExports.pending, (state) => {
        state.exportsStatus = 'pending'
      })
      .addCase(fetchExports.rejected, (state) => {
        state.exportsStatus = 'rejected'
      })
      .addCase(fetchExports.fulfilled, (state, action) => {
        state.exportsStatus = 'fulfilled'
        state.exports = action.payload
      })
      .addCase(fetchOutstandingExports.pending, (state) => {
        state.outstandingExportsStatus = 'pending'
      })
      .addCase(fetchOutstandingExports.rejected, (state) => {
        state.outstandingExportsStatus = 'rejected'
      })
      .addCase(fetchOutstandingExports.fulfilled, (state, action) => {
        state.outstandingExportsStatus = 'fulfilled'
        state.outstandingExports = action.payload
      })
  },
})

export let {
  linkedServicesChanged,
  linkedServicesStatusChanged,
  accessLevelsChanged,
  accessLevelsStatusChanged,
  accessLevelUsersChanged,
  accessLevelUsersStatusChanged,
  accessLevelLinkedServicesChanged,
  accessLevelLinkedServicesStatusChanged,
  accessLevelUsersCountChartChanged,
  accessLevelUsersCountChartStatusChanged,
  accessLevelLinkedServicesChartChanged,
  accessLevelLinkedServicesChartStatusChanged,
  dashboardChartExpandedChanged,
  errorMessageChanged,
  successMessageChanged,
  selectedLinkedServiceChanged,
  selectedConnectClientChanged,
  usersChanged,
  resetUsers,
  usersStatusChanged,
  profilImagesChanged,
  profilImagesStatusChanged,
  linkedServiceUsersChanged,
  linkedServiceUsersStatusChanged,
  linkedServiceUserRemoved,
  userRolesChanged,
  userRolesStatusChanged,
  userCertificatesChanged,
  userCerificatesStatusChanged,
  classesChanged,
  classesStatusChanged,
  coursesChanged,
  coursesStatusChanged,
  userClassEventsChanged,
  userClassEventsStatusChanged,
  userQuizResultsChanged,
  userQuizResultsStatusChanged,
  liveClassesChanged,
  liveClassesStatusChanged,
  liveClassSessionsChanged,
  liveClassSessionsStatusChanged,
} = adminSlice.actions

export let selectLinkedServicesStatus = (state: AppRootState) =>
  state.admin.linkedServicesStatus
export let selectLinkedServices = (state: AppRootState) =>
  state.admin.linkedServices
export let selectLinkedServiceById = (id: string) => (state: AppRootState) =>
  state.admin.linkedServices.find((x) => x.id === id)
export let selectLinkedServiceByName =
  (name: string) => (state: AppRootState) =>
    state.admin.linkedServices.find((x) => x.name === name)

export let selectAccessLevelsStatus = (state: AppRootState) =>
  state.admin.accessLevelsStatus
export let selectAccessLevels = (state: AppRootState) =>
  state.admin.accessLevels
export let selectBoundTreeLimitedAccessLevel = (state: AppRootState) =>
  state.admin.accessLevels.find((x) => x.name.includes('boundTreeLimited'))
export let selectBoundTreeFullAccessLevel = (state: AppRootState) =>
  state.admin.accessLevels.find((x) => x.name.includes('boundTreeFull'))

export let selectAccessLevelUsersStatus = (state: AppRootState) =>
  state.admin.accessLevelUsersStatus
export let selectAccessLevelUsers = (state: AppRootState) =>
  state.admin.accessLevelUsers

export let selectAccessLevelLinkedServicesStatus = (state: AppRootState) =>
  state.admin.accessLevelLinkedServicesStatus
export let selectAccessLevelLinkedServices = (state: AppRootState) =>
  state.admin.accessLevelLinkedServices
export let selectAccessLevelLinkedServiceById =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.admin.accessLevelLinkedServices.find(
      (x) => x.linkedServiceID === linkedServiceID,
    )

export let selectAccessLevelUsersCountChartStatus = (state: AppRootState) =>
  state.admin.accessLevelUsersCountChartStatus
export let selectAccessLevelUsersCountChart = (state: AppRootState) =>
  state.admin.accessLevelUsersCountChart
export let selectAccessLevelLinkedServicesCountChartStatus = (
  state: AppRootState,
) => state.admin.accessLevelLinkedServicesCountChartStatus
export let selectAccessLevelLinkedServicesCountChart = (state: AppRootState) =>
  state.admin.accessLevelLinkedServicesCountChart

export let selectDashboardChartExpanded = (state: AppRootState) =>
  state.admin.dashboardChartExpanded

export let selectErrorMessage = (state: AppRootState) =>
  state.admin.errorMessage

export let selectSuccessMessage = (state: AppRootState) =>
  state.admin.successMessage

export let selectSelectedLinkedService = (state: AppRootState) =>
  state.admin.selectedLinkedService

export let selectUsersStatus = (state: AppRootState) => state.admin.usersStatus
export let selectUsers = (state: AppRootState) => state.admin.users
export let selectUserByEmail = (email: string) => (state: AppRootState) =>
  state.admin.users.find((x) => x.primaryEmail === email)

export let selectProfileImagesStatus = (state: AppRootState) =>
  state.admin.profileImagesStatus
export let selectProfileImages = (state: AppRootState) =>
  state.admin.profileImages
export let selectProfileImageByUserID =
  (userID: string) => (state: AppRootState) =>
    state.admin.profileImages.find((x) => x.userID === userID)

export let selectLinkedServiceUsersStatus = (state: AppRootState) =>
  state.admin.linkedServiceUsersStatus
export let selectLinkedServiceUsers = (state: AppRootState) =>
  state.admin.linkedServiceUsers
export let selectLinkedServicesByUserId =
  (userID: string) => (state: AppRootState) =>
    state.admin.linkedServices.filter((ls) =>
      state.admin.linkedServiceUsers
        .filter((x) => x.userID === userID)
        .map((x) => x.linkedServiceID)
        .includes(ls.id),
    )

export let selectLinkedServiceRosterByID =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.admin.linkedServiceRoster[linkedServiceID]
export let selectLinkedServiceRosterStatusByID =
  (linkedServiceID: string) => (state: AppRootState) =>
    state.admin.linkedServiceRosterStatus[linkedServiceID]

export let selectUserRolesStatus = (state: AppRootState) =>
  state.admin.userRolesStatus
export let selectUserRolesByUserId =
  (userID: string) => (state: AppRootState) =>
    state.admin.userRoles.filter((x) => x.userID === userID)

export let selectRolesStatus = (state: AppRootState) => state.admin.rolesStatus
export let selectRoles = (state: AppRootState) => state.admin.roles

export let selectUserCertificatesStatus = (state: AppRootState) =>
  state.admin.userCertificatesStatus
export let selectUserCertificatesByUserId =
  (userID: string) => (state: AppRootState) =>
    state.admin.userCertificates.filter((x) => x.userID === userID)

export let selectClassesStatus = (state: AppRootState) =>
  state.admin.classesStatus
export let selectClasses = (state: AppRootState) => state.admin.classes
export let selectClassesByClassID = (id: string) => (state: AppRootState) =>
  state.admin.classes.find((x) => x.id === id)
export let selectClassesByCourseID = (id: string) => (state: AppRootState) =>
  state.admin.classes.filter((x) => x.courseID === id)

export let selectCoursesStatus = (state: AppRootState) =>
  state.admin.coursesStatus
export let selectCourses = (state: AppRootState) => state.admin.courses
export let selectCourseByID = (id: string) => (state: AppRootState) =>
  state.admin.courses.find((x) => x.id === id)
export let selectCourseByName = (name: string) => (state: AppRootState) =>
  state.admin.courses.find((x) => x.name === name)
export let selectCoursesByNames = (names: string[]) => (state: AppRootState) =>
  state.admin.courses.filter((x) => names.includes(x.name))

export let selectClassesByUserCertificates =
  (userID: string) => (state: AppRootState) =>
    state.admin.classes.filter((c) =>
      state.admin.userCertificates
        .filter((x) => x.userID === userID)
        .map((x) => x.courseID)
        .includes(c.courseID),
    )

export let selectUserClassEventsStatus = (state: AppRootState) =>
  state.admin.classEventsStatus
export let selectUserClassEvents = (state: AppRootState) =>
  state.admin.classEvents
export let selectClassEventsByUserId =
  (userID: string) => (state: AppRootState) =>
    state.admin.classEvents.filter((x) => x.userID === userID)
export let selectClassesByUserClassEvents =
  (userID: string) => (state: AppRootState) =>
    state.admin.classes.filter((c) =>
      state.admin.classEvents
        .filter((x) => x.userID === userID)
        .map((x) => x.classID)
        .includes(c.id),
    )

export let selectUserCourseEventsStatus = (state: AppRootState) =>
  state.admin.courseEventsStatus
export let selectUserCourseEvents = (state: AppRootState) =>
  state.admin.courseEvents
export let selectCourseEventsByUserId =
  (userID: string) => (state: AppRootState) =>
    state.admin.courseEvents.filter((x) => x.userID === userID)
export let selectCoursesByUserCourseEvents =
  (userID: string) => (state: AppRootState) =>
    state.admin.courses.filter((c) =>
      state.admin.courseEvents
        .filter((x) => x.userID === userID)
        .map((x) => x.courseID)
        .includes(c.id),
    )

export let selectUserQuizResultsStatus = (state: AppRootState) =>
  state.admin.quizResultsStatus
export let selectUserQuizResults = (state: AppRootState) =>
  state.admin.quizResults
export let selectUserQuizEventsByUserID =
  (userID: string) => (state: AppRootState) =>
    state.admin.quizResults.filter((x) => x.userID === userID)
export let selectClassesByUserQuizResults =
  (userID: string) => (state: AppRootState) =>
    state.admin.classes.filter((c) =>
      state.admin.quizResults
        .filter((x) => x.userID === userID)
        .map((x) => x.classID)
        .includes(c.id),
    )

export let selectAllVideosStatus = (state: AppRootState) =>
  state.admin.allVideosStatus
export let selectAllVideos = (state: AppRootState) => state.admin.allVideos
export let selectVideoByID = (id: string) => (state: AppRootState) =>
  state.admin.allVideos.find((x) => x.id === id)

export let selectLiveClassesStatus = (state: AppRootState) =>
  state.admin.liveClassesStatus
export let selectLiveClasses = (state: AppRootState) =>
  [...state.admin.liveClasses].sort((a, b) => a.name.localeCompare(b.name))
export let selectLiveClassById = (id: string) => (state: AppRootState) =>
  state.admin.liveClasses.find((x) => x.id === id)
export let selectLiveClassesGroupedByID = (state: AppRootState) =>
  groupBy((x: LiveClass) => x.id)(state.admin.liveClasses)
export let selectLiveClassesByNames =
  (names: string[]) => (state: AppRootState) =>
    state.admin.liveClasses.filter((x) => names.includes(x.name))

export let selectLiveClassSessionsStatus = (state: AppRootState) =>
  state.admin.liveClassSessionsStatus
export let selectLiveClassSessions = (state: AppRootState) =>
  state.admin.liveClassSessions
export let selectLiveClassSessionsByLiveClassId =
  (id: string) => (state: AppRootState) =>
    state.admin.liveClassSessions.filter((x) => x.classID === id)

export let selectLiveClassTemplatesStatus = (state: AppRootState) =>
  state.admin.liveClassTemplatesStatus
export let selectLiveClassTemplates = (state: AppRootState) =>
  state.admin.liveClassTemplates

export let selectLiveClassAccreditationsStatus = (state: AppRootState) =>
  state.admin.liveClassAccreditationsStatus
export let selectLiveClassAccreditationsByCourseID =
  (id: string) => (state: AppRootState) =>
    state.admin.liveClassAccreditations.filter(
      (a) =>
        (a.ceBroker?.courseID ||
          a.virginia?.courseID ||
          a.newYork?.courseID ||
          a.newJersey?.courseID) === id,
    )

export let selectLiveClassCoursesStatus = (state: AppRootState) =>
  state.admin.liveClassCoursesStatus
export let selectLiveClassCourseByCode =
  (id: string) => (state: AppRootState) =>
    state.admin.liveClassCourses.find((lcc) => (lcc as any).code === id)

export let selectLiveClassTitleScreensStatusByID =
  (id: string) => (state: AppRootState) =>
    state.admin.liveClassTitleScreensStatus[id]
export let selectLiveClassTitleScreenByID =
  (id: string) => (state: AppRootState) =>
    state.admin.liveClassTitleScreens.find((ts) => ts.id === id)

export let selectQuizByClassID = (id: string) => (state: AppRootState) =>
  state.admin.liveClassQuizzes[id]
export let selectQuizzesStatus = (state: AppRootState) =>
  state.admin.liveClassQuizzesStatus

export let selectLiveClassSurveys = (state: AppRootState) =>
  state.admin.liveClassSurveys
export let selectLiveClassSurveysStatus = (state: AppRootState) =>
  state.admin.liveClassSurveysStatus

export let selectCourseSurveys = (state: AppRootState) =>
  state.admin.courseSurveys
export let selectCourseSurveysStatus = (state: AppRootState) =>
  state.admin.courseSurveysStatus

export let selectSubscriptionsStatus = (state: AppRootState) =>
  state.admin.subscriptionsStatus
export let selectSubscriptionByUserID =
  (userID: string) => (state: AppRootState) =>
    state.admin.subscriptions.find((s) => s.userID === userID)

export let selectUserAccessLevelsStatus = (state: AppRootState) =>
  state.admin.userAccessLevelStatus
export let selectUserAccessLevelByUserID =
  (userID: string) => (state: AppRootState) =>
    state.admin.userAccessLevels.find((ual) => ual.userID === userID)

export let selectSpecialCertificationsStatus = (state: AppRootState) =>
  state.admin.specialCertificationsStatus
export let selectSpecialCertifications = (state: AppRootState) =>
  state.admin.specialCertifications

export let selectConnectClientsStatus = (state: AppRootState) =>
  state.admin.connectClientsStatus
export let selectConnectClients = (state: AppRootState) =>
  state.admin.connectClients
export let selectConnectClientById = (id: string) => (state: AppRootState) =>
  state.admin.connectClients.find((x) => x.id === id)
export let selectSelectedConnectClient = (state: AppRootState) =>
  state.admin.selectedConnectClient
export let selectConnectClientMappingsStatus =
  (clientID: string) => (state: AppRootState) =>
    state.admin.connectClientMappingsStatus[clientID] || 'uninitialized'
export let selectConnectClientMappings =
  (clientID: string) => (state: AppRootState) =>
    state.admin.connectClientMappings[clientID] || []

export let selectExports = (state: AppRootState) => state.admin.exports
export let selectExportsStatus = (state: AppRootState) =>
  state.admin.exportsStatus
export let selectOutstandingExports = (state: AppRootState) =>
  state.admin.outstandingExports
export let selectOutstandingExportsStatus = (state: AppRootState) =>
  state.admin.outstandingExportsStatus

export let fetchEntitiesOnUsersFetched =
  (store: any) => (next: any) => (action: any) => {
    if (action.type === 'admin/fetchUsers/fulfilled') {
      store.dispatch(fetchProfileImages(action.payload.map((x: User) => x.id)))
      store.dispatch(
        fetchLinkedServiceUsers(action.payload.map((x: User) => x.id)),
      )
      store.dispatch(fetchUserRoles(action.payload.map((x: User) => x.id)))
      store.dispatch(
        fetchUserCertificates(action.payload.map((x: User) => x.id)),
      )
      store.dispatch(
        fetchUserClassEvents(action.payload.map((x: User) => x.id)),
      )
      store.dispatch(
        fetchUserCourseEvents(action.payload.map((x: User) => x.id)),
      )
      store.dispatch(
        fetchUserQuizResults(action.payload.map((x: User) => x.id)),
      )
      store.dispatch(
        fetchUserSubscriptions(action.payload.map((x: User) => x.id)),
      )
      store.dispatch(
        fetchUserAccessLevels(action.payload.map((x: User) => x.id)),
      )
    }

    if (action) {
      return next(action)
    }
  }

export default adminSlice.reducer
